import { getAppNow } from "../lib/get-app-now"
import type { AppModalType } from "../types"

const STORAGE_KEY = "app_modals_show_state"

export type ModalGroup = "event" | "stars" | "reports"

export type ReportTheme =
  | "soulmate_sketch"
  | "astrology"
  | "love_match"
  | "astro_map"
  | "aura_scan"

/**
 * Per-theme progress in the cross-theme report rotation
 * (all themes v1, then all themes v2):
 * 0 = needs v1; 1 = after v1 → needs v2; 2 = after v2 → wait 7d then v1.
 */
export type ReportThemeStage = 0 | 1 | 2

export interface ModalDailyCount {
  day: string
  count: number
}

export interface ModalShowState {
  onboardingAt: string
  /**
   * Last browser session id that recorded a non-gift show (debug / legacy).
   * Visit gating is in-memory via visibility — not this field.
   */
  shownSessionId: string | null
  /** UTC day when shownSessionId was last written (legacy). */
  shownSessionDay: string | null
  daily: ModalDailyCount
  lastShownAtByType: Partial<Record<AppModalType, string>>
  lastShownAtByGroup: Partial<Record<ModalGroup, string>>
  starsShowCount: number
  reportThemeStage: Record<ReportTheme, ReportThemeStage>
}

const DEFAULT_THEME_STAGE: Record<ReportTheme, ReportThemeStage> = {
  soulmate_sketch: 0,
  astrology: 0,
  love_match: 0,
  astro_map: 0,
  aura_scan: 0,
}

function emptyState(onboardingAt: string): ModalShowState {
  return {
    onboardingAt,
    shownSessionId: null,
    shownSessionDay: null,
    daily: { day: onboardingAt, count: 0 },
    lastShownAtByType: {},
    lastShownAtByGroup: {},
    starsShowCount: 0,
    reportThemeStage: { ...DEFAULT_THEME_STAGE },
  }
}

export function toUtcDayKey(d = getAppNow()): string {
  return d.toISOString().slice(0, 10)
}

function readRaw(): ModalShowState | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ModalShowState
  } catch {
    return null
  }
}

function writeRaw(state: ModalShowState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* ignore */
  }
}

/** Ensure onboarding day exists; create state on first webapp visit. */
export function ensureModalShowState(now = getAppNow()): ModalShowState {
  const today = toUtcDayKey(now)
  const existing = readRaw()
  if (existing?.onboardingAt) {
    return {
      ...emptyState(existing.onboardingAt),
      ...existing,
      reportThemeStage: {
        ...DEFAULT_THEME_STAGE,
        ...existing.reportThemeStage,
      },
      lastShownAtByType: existing.lastShownAtByType ?? {},
      lastShownAtByGroup: existing.lastShownAtByGroup ?? {},
    }
  }
  const created = emptyState(today)
  writeRaw(created)
  return created
}

export function getModalShowState(now = getAppNow()): ModalShowState {
  return ensureModalShowState(now)
}

export function getModalGroup(type: AppModalType): ModalGroup | null {
  if (type === "event") return "event"
  if (type === "stars_special_offer_v1" || type === "stars_special_offer_v2") {
    return "stars"
  }
  if (type.startsWith("report_")) return "reports"
  return null
}

export function getReportTheme(type: AppModalType): ReportTheme | null {
  if (type.includes("soulmate_sketch")) return "soulmate_sketch"
  if (type.includes("aura_scan")) return "aura_scan"
  if (type.includes("astrology")) return "astrology"
  if (type.includes("love_match")) return "love_match"
  if (type.includes("astro_map")) return "astro_map"
  return null
}

export function getDailyShowCount(state: ModalShowState, now = getAppNow()): number {
  const today = toUtcDayKey(now)
  if (state.daily.day !== today) return 0
  return state.daily.count
}

/**
 * Record a non-gift modal open: daily caps, type/group timestamps,
 * stars rotation counter, report theme stage.
 */
export function recordModalShown(
  type: AppModalType,
  sessionId: string,
  now = getAppNow(),
): ModalShowState {
  const state = ensureModalShowState(now)
  const today = toUtcDayKey(now)
  const iso = now.toISOString()
  const group = getModalGroup(type)

  const daily: ModalDailyCount =
    state.daily.day === today
      ? { day: today, count: state.daily.count + 1 }
      : { day: today, count: 1 }

  const next: ModalShowState = {
    ...state,
    shownSessionId: sessionId,
    shownSessionDay: today,
    daily,
    lastShownAtByType: { ...state.lastShownAtByType, [type]: iso },
    lastShownAtByGroup: group
      ? { ...state.lastShownAtByGroup, [group]: iso }
      : state.lastShownAtByGroup,
    starsShowCount:
      group === "stars" ? state.starsShowCount + 1 : state.starsShowCount,
    reportThemeStage: { ...state.reportThemeStage },
  }

  const theme = getReportTheme(type)
  if (theme) {
    const isV2 = type.endsWith("_v2")
    if (isV2) {
      next.reportThemeStage[theme] = 2
    } else {
      // New v1 cycle: zero every theme that already finished v2 so later
      // themes (Aura) get a v1 slot instead of being stuck at stage 2.
      if ((state.reportThemeStage[theme] ?? 0) === 2) {
        for (const key of Object.keys(next.reportThemeStage) as ReportTheme[]) {
          if (next.reportThemeStage[key] === 2) next.reportThemeStage[key] = 0
        }
      }
      next.reportThemeStage[theme] = 1
    }
  }

  writeRaw(next)
  return next
}
